import React from 'react';
import styles from './CsvFormatExplainer.module.css';

/**
 * One column of a bulk-CSV upload, for the shared explainer's column
 * reference list. `name` is the (verbatim) header cell — rendered in a
 * <code> chip — and `description` is the per-section help text for it.
 */
export interface CsvColumn {
  /** The literal header cell, e.g. "email", "Schedule Type", "<period>". */
  name: string;
  /** What this column holds / its accepted values. */
  description: React.ReactNode;
}

export interface CsvFormatExplainerProps {
  /** Section heading text (an icon is prepended — see `icon`). */
  title: string;
  /** Emoji prepended to the heading; the shared 📄 by default. */
  icon?: string;
  /**
   * Heading level for `title`, so each panel keeps its own document
   * outline (the calendar panel nests this under an h3 sibling, the others
   * under h4 ones). Defaults to h4.
   */
  headingLevel?: 'h3' | 'h4';
  /** One-sentence description of what this bulk upload does. */
  summary: React.ReactNode;
  /** The literal header/format string, shown in bold (e.g. "email,name,role"). */
  format: React.ReactNode;
  /** Per-column help, rendered as a reference list under the summary. */
  columns: CsvColumn[];
  /** The example CSV content shown verbatim in the <pre> block. */
  example: string;
  /** Optional caveat / sync-behavior note rendered above the example. */
  note?: React.ReactNode;
  /**
   * Visual tone of `note`. 'warning' draws attention to a destructive
   * full-sync caveat; 'info' (default) is a plain note.
   */
  noteTone?: 'info' | 'warning';
}

/**
 * Shared explainer for the three admin bulk-CSV upload sections (User
 * Accounts, Class Sections, Calendar). Each section describes a DIFFERENT
 * CSV schema, so this unifies only the presentation and wording *pattern* —
 * a heading, a one-line summary plus the bold format string, a per-column
 * reference list, an optional caveat note, and an example block — while the
 * factual specifics (columns, example, notes) come in as props. Rendered at
 * the top of each panel's existing `.csv-upload-section` wrapper, above the
 * file-upload / paste controls that stay in the panel.
 */
const CsvFormatExplainer = ({
  title,
  icon = '📄',
  headingLevel = 'h4',
  summary,
  format,
  columns,
  example,
  note,
  noteTone = 'info',
}: CsvFormatExplainerProps): React.JSX.Element => {
  const Heading = headingLevel;

  return (
    <>
      <Heading>
        {icon} {title}
      </Heading>
      <p className={styles['csv-upload-paragraph']}>
        {summary} Required format: <strong>{format}</strong>
      </p>
      <ul className={styles['csv-column-list']}>
        {columns.map((column) => (
          <li key={column.name}>
            <code>{column.name}</code> — {column.description}
          </li>
        ))}
      </ul>
      {note && (
        <p
          className={`${styles['csv-upload-paragraph']} ${
            noteTone === 'warning'
              ? styles['csv-explainer-note-warning']
              : styles['csv-explainer-note']
          }`}
        >
          {note}
        </p>
      )}
      <div className={styles['csv-format-example']}>
        <h5>Example CSV format:</h5>
        <pre>{example}</pre>
      </div>
    </>
  );
};

export default CsvFormatExplainer;
